import {wrapError} from '@northpoint-development/labs-common-core';
import {refreshFeatures, retrieveFieldLabels} from '@/services/BackendService';
import {Feature} from '../OverlaysService';
import {SharedMap} from '@/lib/classes/SharedMap';
import {
  FeatureSourceData,
  FieldOrders,
  RefreshFieldSettings,
  RefreshResponse,
  RefreshTab,
  RetrieveLabelsResponse,
} from './types';

/**
 * Maps UI display names to raw comp types used in BMAPS.
 * The refresh settings UI uses display names (e.g. 'Active Lease Deals'),
 * but the cloud function expects raw comp types (e.g. 'Active Lease Deal').
 */
const displayNameToCompType: Record<string, string> = {
  'Active Lease Deals': 'Active Lease Deal',
  'Leases': 'Lease',
  'Land Sales': 'Land Sale',
  'Building Sales': 'Building Sale',
  'Capitals': 'Capital',
};

/**
 * Converts a display name to its raw comp type for data matching.
 * Falls back to the display name if no mapping exists.
 */
const toRawCompType = (displayName: string): string => {
  return displayNameToCompType[displayName] || displayName;
};

// The fields each marker's Beyond Maps source offers today, checked where the export selected them
const fieldLabelsFor = async (markerFeatures: Feature[]): Promise<RetrieveLabelsResponse> => {
  const features: Record<string, FeatureSourceData> = {};
  for (const feature of markerFeatures) {
    if (!feature.originalFeatureMetadata) continue;
    features[feature.id] = {
      originalFeatureMetadata: feature.originalFeatureMetadata,
      fieldSelections: feature.fieldSelections,
    };
  }
  try {
    return await retrieveFieldLabels(features);
  } catch (error) {
    throw wrapError(error, 'Error calling retrieveFieldLabels');
  }
};

const getRefreshFeatureSettings = async (sharedMap: SharedMap) : Promise<{
  tabs: RefreshTab[];
  refreshFieldSettings: RefreshFieldSettings;
}> => {
  const markerFeatures = [...sharedMap.features.values()].filter(
    (f) => f.featureType === 'placeMarker' && f.originalFeatureMetadata,
  );
  if (!markerFeatures.length) {
    return {
      tabs: [],
      refreshFieldSettings: new Map(),
    };
  }

  const {tabs, refreshFieldSettings} = await fieldLabelsFor(markerFeatures);

  const refreshFieldSettingsMap: RefreshFieldSettings = new Map();
  Object.entries(refreshFieldSettings).forEach(([tabId, settings]) => {
    refreshFieldSettingsMap.set(tabId, settings);
  });

  const tabsArray: RefreshTab[] = [];
  Object.entries(tabs).forEach(([tabId, icon]) => {
    tabsArray.push({
      id: tabId,
      icon: {name: icon},
    });
  });

  return {
    tabs: tabsArray,
    refreshFieldSettings: refreshFieldSettingsMap,
  };
};

const refreshMarkers = async (sharedMap: SharedMap, refreshFieldSettings: RefreshFieldSettings) => {
  const markerFeatures = [...sharedMap.features.values()].filter(
    (f) => f.featureType === 'placeMarker' && f.originalFeatureMetadata,
  );
  if (!markerFeatures.length) {
    return {
      summary: {
        total: 0,
        successes: 0,
        errors: 0,
        skipped: 0,
      },
      results: [],
    } as RefreshResponse;
  }
  const features = Object.fromEntries(
    markerFeatures
      .map((f) => {
        // Before refreshing the features, update the field selections based on the user's choices in the dialog
        const tabId = f.originalFeatureMetadata?.tabId;
        if (!tabId || !refreshFieldSettings.has(tabId)) {
          return [f.id, {
            originalFeatureMetadata: f.originalFeatureMetadata,
            fieldSelections: f.fieldSelections,
          }];
        }

        // Update the field selections for this feature based on the user's choices in the dialog
        const selectedFields = refreshFieldSettings.get(tabId);
        f.fieldSelections = {
          displayFieldLabels: selectedFields
            ?.filter((field) => field.value && !field.children)
            ?.map((field) => field.label) || [],
          sections: selectedFields?.filter((field) => field.children).map((field) => ({
            section: toRawCompType(field.label), // Convert display name to raw comp type
            displayFieldLabels: field.children?.filter((child) => child.value).map((child) => child.label) || [],
          })) || [],
        };

        return [
          f.id,
          {
            originalFeatureMetadata: f.originalFeatureMetadata,
            fieldSelections: f.fieldSelections,
          },
        ];
      }),
  );

  /**
   * Tracks the order of top level fields and sections to be ordered in the info window
   * when the features are refreshed.
   */
  const fieldOrders: FieldOrders = {};

  refreshFieldSettings.forEach((settings, tabId) => {
    const displayFieldOrder = settings.filter((s) => !s.children).map((s) => s.label);

    const sectionOrders = settings
      .filter((s) => s.children)
      .map((section) => ({
        section: toRawCompType(section.label), // Convert display name to raw comp type
        displayFieldOrder: section.children?.map((child) => child.label) || [],
      }));

    fieldOrders[tabId] = {
      displayFieldOrder,
      sectionOrders,
    };
  });

  // Update the features in Firestore
  let refreshFeaturesResponse: RefreshResponse;
  try {
    refreshFeaturesResponse = await refreshFeatures({smapId: sharedMap.id, features, fieldOrders});
  } catch (error) {
    throw wrapError(error, 'Error calling refreshFeatures');
  }

  // Update the features locally to match what was sent to the cloud function
  try {
    await sharedMap.refreshFeatures();
  } catch (error) {
    throw wrapError(error, 'Error refreshing features');
  }

  return refreshFeaturesResponse;
};

export {
  getRefreshFeatureSettings,
  refreshMarkers,
};
